import React, { useState, useEffect } from "react";
import { useTranslator } from "../../context/translator";
import { useNotifications } from "../../context/Notifications";
import TemplateList from "../templateList.jsx/TemplateList";
import NewTemplateModal from "../newTemplateModal/NewTemplateModal";
import EmailTemplateWizard from "../emailTemplateWizard/EmailTemplateWizard";
import TemplateEditor from "../templateEditor/TemplateEditor";
import { useSelector } from "react-redux";

import {
  useGetCompanyQuery,
  useGetUserByIdQuery,
  useInsertTemplateMutation,
  useUpdateTemplateMutation,
} from "store/api/apiSlice";

const Templates = ({
  emailTemplates,
  smsTemplates,
  headerTemplates,
  footerTemplates,
}) => {
  const { translate } = useTranslator();
  const { addNotification } = useNotifications();
  const { user } = useSelector((state) => state.user);

  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );

  // RTK Query mutations
  const [insertTemplateMutation] = useInsertTemplateMutation();
  const [updateTemplateMutation] = useUpdateTemplateMutation();

  const [type, setType] = useState("email");
  const [selected, setSelected] = useState(null);
  const [openNewModal, setOpenNewModal] = useState(false);
  const [templates, setTemplate] = useState(emailTemplates);

  useEffect(() => {
    setSelected(null);
  }, [type]);

  useEffect(() => {
    if (type === "email") {
      setTemplate(emailTemplates);
    } else {
      setTemplate(smsTemplates);
    }
  }, [type, emailTemplates, smsTemplates]);

  const handleCreate = async (newTemplate) => {
    try {
      await insertTemplateMutation(newTemplate).unwrap();

      addNotification({
        id: Date.now(),
        message: translate("template-saved"),
        kind: "success",
      });
    } catch (err) {
      console.error(err);

      addNotification({
        id: Date.now(),
        message: translate("template-save-error"),
        kind: "danger",
      });
    }
  };

  const handleUpdate = async (updatedTemplate) => {
    try {
      await updateTemplateMutation({
        templateId: updatedTemplate?._id,
        body: updatedTemplate,
      }).unwrap();

      addNotification({
        id: Date.now(),
        message: translate("template-saved"),
        kind: "success",
      });
    } catch (err) {
      console.error(err);

      addNotification({
        id: Date.now(),
        message: translate("template-save-error"),
        kind: "danger",
      });
    }
  };

  const handleSave = async (template) => {
    if (selected?._id) {
      await handleUpdate(template);
    } else {
      await handleCreate({
        ...template,
        companyId: company?._id,
      });
    }
  };

  if (userLoading || companyLoading) return null;

  return (
    <div className="flex w-full h-full dark:bg-gray-900 text-black dark:text-white">
      {/* Left Panel */}
      <div className="w-1/4 border-r dark:border-gray-700 p-4">
        <div className="flex gap-3 mb-4">
          <button
            onClick={() => setType("email")}
            className={`px-3 py-2 rounded-lg ${
              type === "email"
                ? "bg-blue-600 text-white"
                : "bg-gray-300 dark:bg-gray-700"
            }`}
          >
            {translate("email")}
          </button>

          <button
            onClick={() => setType("sms")}
            className={`px-3 py-2 rounded-lg ${
              type === "sms"
                ? "bg-blue-600 text-white"
                : "bg-gray-300 dark:bg-gray-700"
            }`}
          >
            SMS
          </button>
        </div>

        <TemplateList
          templates={templates}
          type={type}
          selected={selected}
          setSelected={(t) => setSelected({ ...t })}
        />

        <button
          onClick={() => setOpenNewModal(true)}
          className="w-full mt-4 py-2 bg-green-600 text-white rounded-lg"
        >
          {translate("new-template")}
        </button>
      </div>

      {/* Right Panel */}
      <div className="w-3/4 p-4">
        {selected ? (
          type === "email" ? (
            <EmailTemplateWizard
              template={selected}
              headerTemplates={headerTemplates}
              footerTemplates={footerTemplates}
              onSave={handleSave}
            />
          ) : (
            <TemplateEditor
              template={selected}
              type={type}
              onSave={handleSave}
            />
          )
        ) : (
          <p className="text-gray-600 dark:text-gray-300">
            {translate("select-a-template")}
          </p>
        )}
      </div>

      {openNewModal && (
        <NewTemplateModal
          close={() => setOpenNewModal(false)}
          type={type}
          onCreate={(t) => {
            setSelected(t);
            setOpenNewModal(false);
          }}
        />
      )}
    </div>
  );
};

export default Templates;
